import React from 'react';
import { Linkedin, ArrowRight, Mail, Phone } from 'lucide-react';
import './index.css';

const App = () => {
  // Removed state and effect for slideshow functionality

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-black text-[#EAEAEA] font-sans selection:bg-[#E6DCC3] selection:text-black flex flex-col">
      
      {/* Import Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&family=Inter:wght@300;400;500&display=swap');
        
        .font-serif-display {
          font-family: 'Playfair Display', serif;
        }
        .font-sans-body {
          font-family: 'Inter', sans-serif;
        }
        
        /* Custom scrollbar for webkit */
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #000; 
        }
        ::-webkit-scrollbar-thumb {
          background: #333; 
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #555; 
        }
      `}</style>

      {/* --- HERO SECTION --- */}
      <section className="h-screen flex flex-col relative z-10">
        
        {/* Header */}
        <header className="px-6 py-6 md:px-12 lg:px-16 flex flex-col gap-6 z-30 absolute top-0 left-0 w-full bg-gradient-to-b from-black/60 to-transparent">
          <div className="border-b border-white/10 pb-4 flex justify-between items-end">
            <h1 className="text-3xl md:text-4xl font-serif-display tracking-tight text-white">
              Alalu Capital
            </h1>
          </div>
          
          <nav>
            <ul className="flex flex-wrap gap-8 text-sm md:text-base text-neutral-400 font-sans-body tracking-wide uppercase">
              <li>
                <button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors duration-300 tracking-widest">
                  About
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors duration-300 tracking-widest">
                  Contact
                </button>
              </li>
            </ul>
          </nav>
        </header>

        {/* Main Hero Content */}
        <main className="flex-grow flex flex-col items-center justify-between relative w-full max-w-[1400px] mx-auto overflow-hidden pt-24">
          
          {/* Image Container */}
          <div className="relative w-full flex-grow flex items-center justify-center my-2">
            {/* Increased container size for a bigger image look, removed glow, removed slideshow loop */}
            <div className="relative w-full h-full md:w-4/5 lg:w-3/4 max-w-5xl flex items-center justify-center">
              
              {/* Single Image Display */}
              <div className="absolute inset-0 w-full h-full z-10">
                <img 
                  src="image1.png"
                  alt="Hero display"
                  className="w-full h-full object-contain drop-shadow-2xl"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentNode.classList.add('flex', 'items-center', 'justify-center', 'border', 'border-neutral-800', 'bg-neutral-900/50');
                    e.target.parentNode.innerHTML = `<span class="text-neutral-600 font-serif-display italic">Image 1 Placeholder</span>`;
                  }}
                />
              </div>
              
              {/* Fade to text overlay */}
              <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black via-black/80 to-transparent z-20"></div>
            </div>
          </div>

          {/* Headline Typography & CTA */}
          <div className="text-center space-y-4 pb-12 z-30 px-4 mt-[-2rem]">
            <div className="space-y-1">
              <h2 className="text-5xl md:text-7xl lg:text-8xl font-serif-display text-[#EAEAEA] leading-none">
                We invest in the
              </h2>
              <h2 className="text-5xl md:text-7xl lg:text-8xl font-serif-display italic text-[#EAEAEA] leading-none">
                Different.
              </h2>
            </div>

            <div className="pt-8">
              <button 
                onClick={() => scrollToSection('about')}
                className="bg-[#E6DCC3] text-black px-10 py-4 text-xs md:text-sm font-sans-body font-semibold tracking-[0.2em] uppercase hover:bg-white transition-all duration-300 hover:scale-105"
              >
                Learn More
              </button>
            </div>
          </div>

        </main>
      </section>

      {/* --- ABOUT SECTION --- */}
      <section id="about" className="py-24 px-6 md:px-12 lg:px-16 bg-[#0a0a0a] border-t border-neutral-900">
        <div className="max-w-4xl mx-auto space-y-12">
          <div className="flex flex-col gap-2">
            <span className="text-[#E6DCC3] text-xs tracking-widest uppercase font-sans-body">About Alalu</span>
            <h3 className="text-3xl md:text-4xl font-serif-display text-white leading-tight">
              For the innovators, for the brave.
            </h3>
          </div>

          <div className="space-y-8 text-lg md:text-2xl font-serif-display text-neutral-300 leading-relaxed font-light">
            <p>
              In the old Mesopotamian myth, King Alalu held the throne before the world stabilized.
            </p>
            <p>
              <span className="text-white">In modern markets, the investor who moves early defines the structure everyone else has to follow.</span>
            </p>
            <p className="pl-4 md:pl-8 border-l border-[#E6DCC3]/30">
              We move cash-ready, fast and early, so you can set the terms of your exit instead of reacting to outside pressure.
            </p>
            <p>
              We acquire steady, durable B2B service firms from owners who built their companies by hand.
            </p>
            <p className="text-white italic text-xl md:text-3xl">
              You built something real, and now you want a buyer who understands what it took to get here.
            </p>
          </div>
        </div>
      </section>

      {/* --- CONTACT SECTION --- */}
      <section id="contact" className="py-24 px-6 md:px-12 lg:px-16 bg-black relative">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-neutral-800 to-transparent"></div>
        
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <span className="text-[#E6DCC3] text-xs tracking-widest uppercase font-sans-body">Get in Touch</span>
              <h3 className="text-4xl md:text-5xl font-serif-display text-white mt-4">Start the conversation.</h3>
            </div>
            
            <div className="space-y-6 pt-4">
              <div className="flex items-center gap-4 group">
                <div className="w-10 h-10 rounded-full bg-neutral-900 flex items-center justify-center group-hover:bg-[#E6DCC3] transition-colors duration-300">
                  <Mail size={18} className="text-neutral-400 group-hover:text-black" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-neutral-500 uppercase tracking-wide">Email</span>
                  <a href="mailto:princeton@alalu.co" className="text-xl text-white font-serif-display hover:text-[#E6DCC3] transition-colors">princeton@alalu.co</a>
                </div>
              </div>

              <div className="flex items-center gap-4 group">
                <div className="w-10 h-10 rounded-full bg-neutral-900 flex items-center justify-center group-hover:bg-[#E6DCC3] transition-colors duration-300">
                  <Phone size={18} className="text-neutral-400 group-hover:text-black" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-neutral-500 uppercase tracking-wide">Phone</span>
                  <a href="tel:4256815069" className="text-xl text-white font-serif-display hover:text-[#E6DCC3] transition-colors">(425) 681-5069</a>
                </div>
              </div>
            </div>
          </div>

{/* Contact Form */}
<form
  name="contact"
  method="POST"
  action="/success.html"
  data-netlify="true"
  netlify-honeypot="bot-field"
  className="space-y-6"
>
  {/* Netlify hidden fields */}
  <input type="hidden" name="form-name" value="contact" />
  <input type="hidden" name="bot-field" />

  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <div className="space-y-2">
      <label className="text-xs text-neutral-500 uppercase tracking-wide">
        Name
      </label>
      <input
        type="text"
        name="name"
        className="w-full bg-black border border-neutral-800 p-3 text-white focus:outline-none focus:border-[#E6DCC3] transition-colors text-sm font-sans-body"
        placeholder="John Doe"
        required
      />
    </div>

    <div className="space-y-2">
      <label className="text-xs text-neutral-500 uppercase tracking-wide">
        Company
      </label>
      <input
        type="text"
        name="company"
        className="w-full bg-black border border-neutral-800 p-3 text-white focus:outline-none focus:border-[#E6DCC3] transition-colors text-sm font-sans-body"
        placeholder="Acme Inc."
      />
    </div>
  </div>

  <div className="space-y-2">
    <label className="text-xs text-neutral-500 uppercase tracking-wide">
      Email Address
    </label>
    <input
      type="email"
      name="email"
      className="w-full bg-black border border-neutral-800 p-3 text-white focus:outline-none focus:border-[#E6DCC3] transition-colors text-sm font-sans-body"
      placeholder="john@example.com"
      required
    />
  </div>

  <div className="space-y-2">
    <label className="text-xs text-neutral-500 uppercase tracking-wide">
      Message
    </label>
    <textarea
      name="message"
      className="w-full bg-black border border-neutral-800 p-3 text-white focus:outline-none focus:border-[#E6DCC3] transition-colors text-sm font-sans-body min-h-[120px]"
      placeholder="Tell us about your business..."
      required
    ></textarea>
  </div>

  <button className="w-full bg-white text-black py-4 font-bold text-xs tracking-widest uppercase hover:bg-[#E6DCC3] transition-colors flex items-center justify-center gap-2">
    Send Message <ArrowRight size={16} />
  </button>
</form>
</div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="px-6 py-8 md:px-12 lg:px-16 border-t border-neutral-900 flex flex-col md:flex-row justify-between items-center gap-6 text-neutral-600 text-xs font-sans-body bg-black">
        <div>
          © 2025 Alalu Capital. All rights reserved.
        </div>
        
        <div className="flex gap-6 items-center">
          <a href="https://www.linkedin.com/company/alalucapital/" className="hover:text-[#0077b5] transition-colors flex items-center gap-2 group">
            <span className="hidden group-hover:block transition-all duration-300">LinkedIn</span>
            <Linkedin size={20} strokeWidth={1.5} />
          </a>
        </div>
      </footer>

    </div>
  );
};

export default App;
